import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, Typography } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../store/store';
import { setPartnerProfile, setView } from './onboardingSlice';
import { fetchMyPartner, startPartnerAgreement } from './partnerApi';

const PartnerAgreement: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { partnerProfile } = useSelector((s: RootState) => s.onboarding);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const done = partnerProfile?.partner_agreement_status === 'COMPLETED';

  useEffect(() => {
    if (done) return undefined;
    let cancelled = false;
    const pull = async () => {
      try {
        const row = await fetchMyPartner();
        if (cancelled) return;
        dispatch(setPartnerProfile(row));
        if (row?.partner_agreement_status === 'COMPLETED') dispatch(setView('hub'));
      } catch (_) { /* keep polling */ }
    };
    pull();
    const id = window.setInterval(pull, 2000);
    const onFocus = () => { pull(); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => {
      cancelled = true;
      window.clearInterval(id);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, [dispatch, done]);

  const open = async () => {
    setBusy(true);
    setError('');
    try {
      const { distributor, signUrl } = await startPartnerAgreement();
      dispatch(setPartnerProfile(distributor));
      if (signUrl) window.open(signUrl, '_blank', 'noopener');
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Could not open the partner agreement');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box>
      <Button variant="text" onClick={() => dispatch(setView('hub'))} sx={{ mb: 1, px: 0 }}>← Back to overview</Button>
      <Typography sx={{ fontFamily: '"Fraunces", Georgia, serif', fontWeight: 700, fontSize: 28, mb: 1 }}>
        Partner agreement
      </Typography>
      <Typography sx={{ color: 'text.secondary', mb: 2 }}>
        Open the partner agreement, complete your details, then sign. This page updates automatically once the signed document is received.
      </Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {done ? (
        <Alert severity="success">Agreement signed.</Alert>
      ) : (
        <>
          <Button variant="contained" onClick={open} disabled={busy}>
            {busy ? 'Opening…' : 'Open partner agreement'}
          </Button>
          {partnerProfile?.partner_agreement_status === 'IN_PROGRESS' && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Waiting for Zoho to confirm the signature. This page updates automatically — no refresh needed.
            </Alert>
          )}
        </>
      )}
    </Box>
  );
};

export default PartnerAgreement;
